import { supabase } from "@/lib/supabase";
import apiClient from "@/lib/api-client";
import { publicReadForViewer, type PublicRead } from "@/lib/public-catalog-columns";
import { deletePlaylistVideoFiles } from "@/lib/playlist-video";
import { uploadVideoToMedia } from "@/lib/video-upload";
import { Track } from "@/types/track";
import { SearchFilters } from "@/types/search-filters";

// Database playlist interface (matches existing Supabase schema)
// Mirrors the playlists table; nullability matches the DB columns.
export interface DatabasePlaylist {
  id: string;
  name: string; // Using 'name' instead of 'title' to match existing schema
  description?: string | null;
  cover_image_url?: string | null; // Using 'cover_image_url' instead of 'cover_url'
  user_id: string; // Using 'user_id' instead of 'created_by'
  is_public: boolean | null; // Using 'is_public' instead of 'is_private'
  created_at: string | null;
  updated_at: string | null;
  source_prompt?: string | null;
  search_metadata?: any; // JSON field for AI search metadata
  source?: string | null; // e.g. "disco_import"
  import_status?: string | null;
}

// Frontend playlist interface (includes tracks)
export interface PlaylistWithTracks {
  id: string;
  title: string;
  description?: string;
  coverUrl?: string | null;
  tracks: Track[];
  createdAt: string;
  updatedAt: string;
  isPublic: boolean;
  createdBy: string;
  searchMetadata?: {
    query?: string;
    audioAnalysis?: string;
    videoAnalysis?: string;
    audioSummary?: string;
    videoSummary?: string;
    tags?: string[]; // Deprecated: kept for backwards compatibility
    filters?: SearchFilters; // New: structured filter object
    /** The reference video's path in the `media` bucket (see `src/lib/playlist-video.ts`). */
    videoPath?: string;
    /** Legacy: a 7-day signed URL, stored before videos were kept by path. Read for its path. */
    videoUrl?: string;
    /** A YouTube link from Sol's Save results, canonical. Read it through `playlistVideoLink`. */
    videoLink?: string;
    audioUrl?: string;
  };
  source?: string;
  import_status?: string | null;
}

// Convert database playlist to frontend format
function mapDatabasePlaylistToFrontend(
  dbPlaylist: DatabasePlaylist,
  tracks: Track[] = []
): PlaylistWithTracks {
  return {
    id: dbPlaylist.id,
    title: dbPlaylist.name, // Map 'name' to 'title'
    description: dbPlaylist.description ?? undefined,
    coverUrl: dbPlaylist.cover_image_url, // Map 'cover_image_url' to 'coverUrl'
    tracks,
    createdAt: dbPlaylist.created_at ?? "",
    updatedAt: dbPlaylist.updated_at ?? "",
    isPublic: dbPlaylist.is_public ?? false, // Map 'is_public' directly
    createdBy: dbPlaylist.user_id, // Map 'user_id' to 'createdBy'
    searchMetadata: dbPlaylist.search_metadata, // Pass through search metadata
    source: dbPlaylist.source ?? undefined,
    import_status: dbPlaylist.import_status,
  };
}

// Convert database track to frontend format
async function mapDatabaseTrackToFrontend(dbTrack: any): Promise<Track> {
  // For cover art, we can keep the original URL as it's usually public
  let signedCoverArtUrl = dbTrack.cover_art_url || dbTrack.artwork_url;

  // Also check for other possible image field names
  if (!signedCoverArtUrl) {
    signedCoverArtUrl =
      dbTrack.cover_image_url || dbTrack.artwork_image_url || dbTrack.image_url;
  }

  const normalizedTags = Array.isArray(dbTrack.tags) ? dbTrack.tags : [];
  const normalizedWriters = Array.isArray(dbTrack.writers)
    ? dbTrack.writers
    : [];
  const normalizedLyrics = dbTrack.lyrics || "";

  return {
    // Required fields
    url: "", // Playback uses backend signed URL endpoint
    id: dbTrack.id,
    created_at: dbTrack.created_at,
    updated_at: dbTrack.updated_at,
    title: dbTrack.title || "Unknown Title",
    user_id: dbTrack.user_id,
    is_published: dbTrack.is_published,
    artist: dbTrack.artist || "",
    progress: 100,
    lastModified: dbTrack.updated_at || dbTrack.created_at,
    duration:
      typeof dbTrack.duration === "string"
        ? dbTrack.duration
        : String(dbTrack.duration || ""),
    metadata: dbTrack.metadata || {},
    tags: normalizedTags,
    writers: normalizedWriters,
    lyrics: normalizedLyrics,
    analysis: dbTrack.analysis || null, // Add the required 'analysis' property

    // Optional/legacy fields
    genre: Array.isArray(dbTrack.genre)
      ? dbTrack.genre.join(", ")
      : dbTrack.genre || "Unknown",
    bpm: dbTrack.bpm,
    key: dbTrack.key,
    moods: dbTrack.moods || [],
    instruments: dbTrack.instruments || [],
    vocal_gender: dbTrack.vocal_gender,
    vocal_type: dbTrack.vocal_type,
    description: dbTrack.description,
    analysis_status: dbTrack.analysis_status,
    subgenre: dbTrack.subgenre || "",
    explicit_content: dbTrack.explicit_content || false,
    emotional_arc: dbTrack.emotional_arc || "",
    language: dbTrack.language || "",
    harmony: dbTrack.harmony || "",
    chord_progression: dbTrack.chord_progression || "",
    lyrical_theme: dbTrack.lyrical_theme || "",
    cultural_fusion: dbTrack.cultural_fusion || "",
    historical_period: dbTrack.historical_period || "",
    cover_art_url: signedCoverArtUrl,
    artwork_url: signedCoverArtUrl,
    album: dbTrack.album,
    isrc: dbTrack.isrc,
    upc: dbTrack.upc,
    release_date: dbTrack.release_date,
    licensing_tier: dbTrack.licensing_tier,
    pricing_method: dbTrack.pricing_method,
    fixed_price: dbTrack.fixed_price,
    min_price: dbTrack.min_price,
    max_price: dbTrack.max_price,
    sweet_spot_price: dbTrack.sweet_spot_price,
    allowed_usage_types: dbTrack.allowed_usage_types,
    licensing_restrictions: dbTrack.licensing_restrictions,
    mixes: dbTrack.mixes,
    parentTrackId: dbTrack.parentTrackId,
    syncInfo: dbTrack.syncInfo,
    territories_included: dbTrack.territories_included || [],
    grouping: dbTrack.grouping ?? null,
    contact: dbTrack.contact ?? null,
    rights: dbTrack.rights,
    tempo: dbTrack.tempo,
    energy: dbTrack.energy,
    status: dbTrack.status,
    analysis_method: dbTrack.analysis_method,
    bpm_confidence: dbTrack.bpm_confidence,
    key_confidence: dbTrack.key_confidence,
    energy_confidence: dbTrack.energy_confidence,
    confidence_scores: dbTrack.confidence_scores,
    clap_success: dbTrack.clap_success,
  };
}

export class SupabasePlaylistService {
  // Upload file to Supabase Storage
  static async uploadFile(
    file: File,
    bucket: string,
    path: string
  ): Promise<string | null> {
    try {
      const { data, error } = await supabase.storage
        .from(bucket)
        .upload(path, file, {
          cacheControl: "3600",
          upsert: false,
        });

      if (error) {
        console.error("Error uploading file:", error);
        return null;
      }

      // Prefer a signed URL (works for private buckets). If that fails, fall back to public URL.
      try {
        const { data: signed } = await supabase.storage
          .from(bucket)
          .createSignedUrl(data.path, 60 * 60 * 24 * 7); // 7 days
        if (signed?.signedUrl) return signed.signedUrl;
      } catch (e) {
        // no-op; will fall back to public URL below
      }

      const {
        data: { publicUrl },
      } = supabase.storage.from(bucket).getPublicUrl(data.path);
      return publicUrl;
    } catch (error) {
      console.error("Error in uploadFile:", error);
      return null;
    }
  }

  // Get all playlists for current user (joined with tracks to avoid N+1 calls)
  static async getUserPlaylists(): Promise<PlaylistWithTracks[]> {
    try {
      // Get current user
      const {
        data: { user },
        error: userError,
      } = await supabase.auth.getUser();
      if (userError || !user) {
        console.warn("No authenticated user found");
        return [];
      }

      // Single query: playlists with nested playlist_tracks -> tracks
      const { data: playlists, error: playlistsError } = await supabase
        .from("playlists")
        .select(
          `
          id,
          name,
          description,
          cover_image_url,
          user_id,
          is_public,
          created_at,
          updated_at,
          source_prompt,
          search_metadata,
          source,
          import_status,
          playlist_tracks (
            added_at,
            position,
            tracks ( * )
          )
        `
        )
        .eq("user_id", user.id)
        .order("created_at", { ascending: false });

      if (playlistsError) {
        console.error("Error fetching playlists:", playlistsError);
        return [];
      }

      // Map nested tracks without additional round-trips
      const playlistsWithTracks = await Promise.all(
        (playlists || []).map(async (playlist: any) => {
          const pt = Array.isArray(playlist.playlist_tracks)
            ? playlist.playlist_tracks
            : [];
          // Sort by position if present, else by added_at
          pt.sort((a: any, b: any) => {
            const posA = a?.position ?? Number.MAX_SAFE_INTEGER;
            const posB = b?.position ?? Number.MAX_SAFE_INTEGER;
            if (posA !== posB) return posA - posB;
            return (
              new Date(a?.added_at || 0).getTime() -
              new Date(b?.added_at || 0).getTime()
            );
          });

          const tracks: Track[] = await Promise.all(
            pt
              .map((row: any) => row?.tracks)
              .filter(Boolean)
              .map((t: any) => mapDatabaseTrackToFrontend(t))
          );

          // Drop the embedded to avoid leaking unexpected shape
          const cleanPlaylist: DatabasePlaylist = {
            id: playlist.id,
            name: playlist.name,
            description: playlist.description,
            cover_image_url: playlist.cover_image_url,
            user_id: playlist.user_id,
            is_public: playlist.is_public,
            created_at: playlist.created_at,
            updated_at: playlist.updated_at,
            source_prompt: playlist.source_prompt,
            search_metadata: playlist.search_metadata,
            source: playlist.source,
            import_status: playlist.import_status,
          };

          return mapDatabasePlaylistToFrontend(cleanPlaylist, tracks);
        })
      );

      return playlistsWithTracks;
    } catch (error) {
      console.error("Error in getUserPlaylists:", error);
      return [];
    }
  }

  // Get a public playlist by ID (works without authentication)
  static async getPublicPlaylistById(
    playlistId: string
  ): Promise<PlaylistWithTracks | null> {
    try {
      // Whether this playlist carries a public share link is answered by the
      // backend now. It used to be read straight out of `playlist_shares`,
      // which is the only reason the browser needed SELECT on the table that
      // holds every share token — and a token is the credential for one
      // client's private feedback. A boolean crosses the wire instead of the row.
      let isPubliclyShared = false;
      try {
        const status = await apiClient.get<{ is_publicly_shared: boolean }>(
          `/api/playlist-share/status?playlist_id=${encodeURIComponent(playlistId)}`
        );
        isPubliclyShared = Boolean(status?.is_publicly_shared);
      } catch {
        // An unreachable backend must not grant access it could not confirm.
        // `playlist.is_public` below still stands on its own.
        isPubliclyShared = false;
      }

      // Now try to get the playlist itself. A fully set-up account reads it as before; anyone else
      // reads it through the anon-only client without the curator's search brief and the import
      // internals: the database refuses those columns, and `*`, to anon (2026-10-13). The tracks
      // below are read the same way, so the page never mixes the two views.
      const read = await publicReadForViewer();
      const { data: playlist, error: playlistError } = await read.client
        .from("playlists")
        .select<string, DatabasePlaylist>(read.playlistColumns)
        .eq("id", playlistId)
        .maybeSingle();

      // Handle RLS errors
      if (playlistError) {
        // If RLS blocks (406) or other permission error, return null
        if (
          playlistError.code === "PGRST301" ||
          playlistError.code === "42501" ||
          playlistError.code === "406"
        ) {
          return null;
        }
        // If playlist not found and we have a share, still return null (inconsistent state)
        if (playlistError.code === "PGRST116") {
          return null;
        }
        // Other errors
        return null;
      }

      if (!playlist) {
        return null;
      }

      // Check if playlist is accessible:
      // 1. Has a share record with is_public = true
      // 2. OR playlist itself is marked as public
      const isAccessible = isPubliclyShared || playlist.is_public;

      if (!isAccessible) {
        return null;
      }

      // Get tracks for the playlist
      const tracks = await this.getPlaylistTracks(playlistId, read);

      return mapDatabasePlaylistToFrontend(playlist, tracks);
    } catch (error) {
      return null;
    }
  }

  /**
   * The tracks of a playlist. A fully set-up account reads every column through its own session,
   * exactly as before; anyone else only the public columns, through the anon-only client: contact
   * and rights-holder data are for set-up accounts, the database refuses `tracks(*)` to anon and
   * gives an unfinished account no `tracks` rows of its own. `read` is the caller's decision when
   * it already made one (`getPublicPlaylistById`), so the playlist and its tracks agree.
   */
  static async getPlaylistTracks(playlistId: string, read?: PublicRead): Promise<Track[]> {
    try {
      const { client, trackColumns } = read ?? (await publicReadForViewer());
      const { data: playlistTracks, error } = await client
        .from("playlist_tracks")
        .select<string, { track_id: string; added_at: string | null; tracks: Record<string, unknown> | null }>(
          `track_id, added_at, tracks (${trackColumns})`
        )
        .eq("playlist_id", playlistId)
        .order("added_at", { ascending: true });

      if (error) {
        // Handle RLS errors gracefully
        // PGRST301 = insufficient_privilege, 42501 = insufficient_privilege (PostgreSQL), 406 = Not Acceptable
        if (
          error.code === "PGRST301" ||
          error.code === "42501" ||
          error.code === "406" ||
          error.message?.includes("permission") ||
          error.message?.includes("row-level security")
        ) {
          return [];
        }
        return [];
      }

      // Convert to frontend format
      const tracks = (playlistTracks || []).filter((pt) => pt.tracks); // Filter out any tracks that might be null

      return await Promise.all(
        tracks.map((pt) => mapDatabaseTrackToFrontend(pt.tracks))
      );
    } catch (error) {
      return [];
    }
  }

  /**
   * Create a playlist for the signed-in user. Throws when it can't be created (no session, the
   * insert refused, the network), so the caller can say why. A video that can't be uploaded or
   * tracks that can't be saved don't fail it (see below).
   */
  static async createPlaylist(playlistData: {
    title: string;
    description?: string;
    coverUrl?: string | null;
    tracks: Track[];
    isPublic?: boolean;
    searchMetadata?: {
      query?: string;
      audioAnalysis?: string;
      videoAnalysis?: string;
      audioSummary?: string;
      videoSummary?: string;
      tags?: string[]; // Deprecated: kept for backwards compatibility
      filters?: SearchFilters; // New: structured filter object
      // Files to upload first. A video is stored only as the path of the file uploaded here.
      videoFile?: File;
      audioFile?: File;
      audioUrl?: string;
    };
  }): Promise<PlaylistWithTracks> {
    try {
      // Get current user
      const {
        data: { user },
        error: userError,
      } = await supabase.auth.getUser();
      if (userError || !user) {
        throw new Error("User not authenticated");
      }

      // Create playlist using existing schema
      const playlistInsertData: any = {
        name: playlistData.title, // Map 'title' to 'name'
        description: playlistData.description,
        cover_image_url: playlistData.coverUrl, // Map 'coverUrl' to 'cover_image_url'
        user_id: user.id, // Use 'user_id' instead of 'created_by'
        is_public: playlistData.isPublic || false, // Use 'is_public' directly
      };

      // Handle file uploads and prepare search metadata
      const finalSearchMetadata: Record<string, unknown> = {
        ...playlistData.searchMetadata,
      };
      // The video is stored only as the path of a file uploaded below (D12, F22). A URL is never
      // stored, because the 7-day signature kept here before was renewed by nothing. A URL or path
      // handed in is dropped too, so that each video file belongs to one playlist and can be
      // deleted when that playlist's video is replaced (src/lib/playlist-video.ts).
      delete finalSearchMetadata.videoUrl;
      delete finalSearchMetadata.videoPath;

      if (playlistData.searchMetadata) {
        // Upload video file if provided: named by a uuid, never the file's own name. A failed
        // upload still creates the playlist, without the video. The playlist handed back then
        // names no video, which is how CreatePlaylistDialog knows to say so.
        if (playlistData.searchMetadata.videoFile instanceof File) {
          try {
            finalSearchMetadata.videoPath = await uploadVideoToMedia(
              "playlists",
              playlistData.searchMetadata.videoFile
            );
          } catch (error) {
            console.error("Error uploading the playlist's video:", error);
          }
          // Remove the File object from metadata
          delete finalSearchMetadata.videoFile;
        }

        // Upload audio file if provided
        if (playlistData.searchMetadata.audioFile instanceof File) {
          const audioPath = `playlists/${user.id}/${Date.now()}-${
            playlistData.searchMetadata.audioFile.name
          }`;
          const audioUrl = await this.uploadFile(
            playlistData.searchMetadata.audioFile,
            "media",
            audioPath
          );
          if (audioUrl) {
            finalSearchMetadata.audioUrl = audioUrl;
          }
          // Remove the File object from metadata
          delete finalSearchMetadata.audioFile;
        }
      }

      // Only include search_metadata if it exists and has content
      if (finalSearchMetadata && Object.keys(finalSearchMetadata).length > 0) {
        playlistInsertData.search_metadata = finalSearchMetadata;
      }

      const { data: playlist, error: playlistError } = await supabase
        .from("playlists")
        .insert([playlistInsertData])
        .select()
        .single();

      if (playlistError) {
        console.error("Error creating playlist:", playlistError);
        throw playlistError;
      }

      // Add tracks to playlist if any
      if (playlistData.tracks.length > 0) {
        const { error: tracksError } = await supabase
          .from("playlist_tracks")
          .insert(
            playlistData.tracks.map((track) => ({
              playlist_id: playlist.id,
              track_id: String(track.id),
            }))
          );

        if (tracksError) {
          console.error("Error adding tracks to playlist:", tracksError);
          // Don't throw here, playlist was created successfully
        } else {
        }
      }

      return mapDatabasePlaylistToFrontend(playlist, playlistData.tracks);
    } catch (error) {
      console.error("Error in createPlaylist:", error);
      throw error;
    }
  }

  /**
   * Delete one of the signed-in user's playlists, then its video file. The delete hands back the
   * row it removed, so the file deleted is the one that row named (never a stale copy in the
   * store), and nothing is deleted when no row was. The file goes best-effort, inside the owner's
   * folder only (`deletePlaylistVideoFiles`); a failure there is logged and the delete still counts.
   */
  static async deletePlaylist(playlistId: string): Promise<boolean> {
    try {
      const {
        data: { user },
        error: userError,
      } = await supabase.auth.getUser();
      if (userError || !user) {
        throw new Error("User not authenticated");
      }

      const { data: deleted, error } = await supabase
        .from("playlists")
        .delete()
        .eq("id", playlistId)
        .eq("user_id", user.id) // Use 'user_id' instead of 'created_by'
        .select("search_metadata");

      if (error) {
        console.error("Error deleting playlist:", error);
        return false;
      }

      for (const row of deleted ?? []) {
        await deletePlaylistVideoFiles(row.search_metadata, user.id);
      }

      return true;
    } catch (error) {
      console.error("Error in deletePlaylist:", error);
      return false;
    }
  }

  // Add track to playlist
  static async addTrackToPlaylist(
    playlistId: string,
    trackId: string
  ): Promise<boolean> {
    try {
      const { error } = await supabase.from("playlist_tracks").insert([
        {
          playlist_id: playlistId,
          track_id: trackId,
        },
      ]);

      if (error) {
        console.error("Error adding track to playlist:", error);
        return false;
      }

      return true;
    } catch (error) {
      console.error("Error in addTrackToPlaylist:", error);
      return false;
    }
  }

  /**
   * Fill a playlist that has no tracks yet: one insert, so the tracks land together, in the order
   * given, or not at all. Positions are 1-based like every live row (as in playlist-claim.ts): the
   * table's insert trigger would renumber a 0 as "next free position", giving two tracks the same.
   */
  static async addTracksToEmptyPlaylist(
    playlistId: string,
    trackIds: string[]
  ): Promise<boolean> {
    if (trackIds.length === 0) return true;
    try {
      const { error } = await supabase.from("playlist_tracks").insert(
        trackIds.map((trackId, index) => ({
          playlist_id: playlistId,
          track_id: trackId,
          position: index + 1,
        }))
      );
      if (error) {
        console.error("Error adding tracks to playlist:", error);
        return false;
      }
      return true;
    } catch (error) {
      console.error("Error in addTracksToEmptyPlaylist:", error);
      return false;
    }
  }

  /**
   * Remove a track from a playlist. True only when a row was actually deleted: under RLS a delete
   * the viewer may not make (anyone but the owner) matches no rows and returns no error, which must
   * not read as success — the caller would drop the track from the screen while it stays.
   */
  static async removeTrackFromPlaylist(
    playlistId: string,
    trackId: string
  ): Promise<boolean> {
    try {
      const { data: removed, error } = await supabase
        .from("playlist_tracks")
        .delete()
        .eq("playlist_id", playlistId)
        .eq("track_id", trackId)
        .select("track_id");

      if (error) {
        console.error("Error removing track from playlist:", error);
        return false;
      }

      if (!removed || removed.length === 0) {
        console.warn("Removing a track from a playlist deleted nothing (not the owner, or already gone)", {
          playlistId,
          trackId,
        });
        return false;
      }

      return true;
    } catch (error) {
      console.error("Error in removeTrackFromPlaylist:", error);
      return false;
    }
  }

  // Update playlist
  static async updatePlaylist(
    playlistId: string,
    data: {
      title?: string;
      description?: string;
      coverUrl?: string | null;
      isPublic?: boolean;
    }
  ): Promise<boolean> {
    try {
      const {
        data: { user },
        error: userError,
      } = await supabase.auth.getUser();
      if (userError || !user) {
        throw new Error("User not authenticated");
      }

      // Prepare update data, mapping frontend fields to database fields
      const updateData: any = {};
      if (data.title !== undefined) updateData.name = data.title;
      if (data.description !== undefined)
        updateData.description = data.description;
      if ("coverUrl" in data)
        updateData.cover_image_url = data.coverUrl || null;
      if (data.isPublic !== undefined) updateData.is_public = data.isPublic;

      const { error } = await supabase
        .from("playlists")
        .update(updateData)
        .eq("id", playlistId)
        .eq("user_id", user.id); // Ensure user owns the playlist

      if (error) {
        console.error("Error updating playlist:", error);
        return false;
      }

      return true;
    } catch (error) {
      console.error("Error in updatePlaylist:", error);
      return false;
    }
  }
}
